import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { businessId, site, socialImageUrl } from "@/lib/site";
import { getSeoRoute } from "@/lib/routes";

const SiteHead = () => {
  const { pathname } = useLocation();
  const route = getSeoRoute(pathname);
  const isNoIndex = pathname === "/thank-you" || !route;
  const title = route?.title ?? "Page Not Found";
  const description = route?.description ?? "The requested page could not be found.";
  const canonical = `${site.siteUrl}${route?.path === "/" ? "/" : route?.path ?? pathname}`;
  const localBusiness = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": businessId,
    name: site.businessName,
    legalName: site.businessName,
    url: site.siteUrl,
    telephone: site.tel,
    email: site.email,
    image: socialImageUrl,
    address: {
      "@type": "PostalAddress",
      addressLocality: site.city,
      addressRegion: site.region,
      postalCode: site.postalCode,
      addressCountry: "US",
    },
    areaServed: site.serviceAreas.map((name) => ({
      "@type": "City",
      name,
      containedInPlace: { "@type": "State", name: "Wisconsin" },
    })),
    foundingDate: site.foundingYear,
    founder: { "@type": "Person", name: site.ownerName },
    openingHoursSpecification: [
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], opens: "07:00", closes: "18:00" },
      { "@type": "OpeningHoursSpecification", dayOfWeek: ["Saturday"], opens: "08:00", closes: "16:00" },
    ],
    sameAs: [site.googleMapsUrl],
  };

  return (
    <Helmet>
      <meta name="robots" content={isNoIndex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta property="og:site_name" content={site.shortName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={socialImageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={socialImageUrl} />
      <script type="application/ld+json">{JSON.stringify(localBusiness)}</script>
    </Helmet>
  );
};

export default SiteHead;
